import type { WizardFormValues } from '@/lib/wizard-draft'
import { wizardIssueKeys } from '@/lib/validators'

export const AGREEMENT_SECTION_KEYS = [
  'profile',
  'employment',
  'wealth',
  'bank',
  'emergency',
  'documents',
] as const

export type AgreementSectionKey = (typeof AGREEMENT_SECTION_KEYS)[number]

const SECTION_REQUIRED_FIELDS: Record<AgreementSectionKey, (keyof WizardFormValues)[]> = {
  profile: [
    'full_name',
    'email',
    'birth_place',
    'birth_date',
    'nationality_code',
    'identity_type',
    'identity_number',
    'gender',
    'mother_maiden_name',
    'address_street',
    'home_postal_code',
    'mobile_phone',
    'home_ownership_status',
    'account_purpose',
  ],
  employment: ['occupation', 'company_name', 'industry_field', 'position', 'years_at_company'],
  wealth: ['annual_income'],
  bank: ['bank_name', 'bank_branch', 'account_number', 'account_holder', 'bank_account_type'],
  emergency: ['emergency_name', 'emergency_phone', 'emergency_relation'],
  documents: ['doc_selfie', 'doc_identity'],
}

function isFieldFilled(value: unknown): boolean {
  if (typeof value === 'string') return value.trim().length > 0
  if (typeof value === 'number') return Number.isFinite(value)
  if (typeof value === 'boolean') return true
  return value != null && value !== ''
}

const SECTION_ISSUE_FIELDS: Record<AgreementSectionKey, string[]> = {
  profile: [
    'full_name',
    'birth_place',
    'birth_date',
    'identity_number',
    'mother_maiden_name',
    'spouse_name',
    'home_postal_code',
    'npwp_number',
    'address_street',
    'home_phone',
    'home_fax',
    'home_ownership_notes',
    'account_purpose_notes',
  ],
  employment: [
    'office_postal_code',
    'occupation_other',
    'company_name',
    'industry_field',
    'position',
    'office_address',
    'office_phone',
    'office_fax',
  ],
  wealth: ['njop_value', 'total_savings', 'total_assets', 'home_location', 'other_assets'],
  bank: [
    'account_number',
    'account_holder',
    'bank_branch',
    'bank_phone',
    'bank_address',
    'bank_city',
    'bank_province',
    'bank_swift',
    'bank_iban',
  ],
  emergency: ['emergency_name', 'emergency_postal_code', 'emergency_address', 'emergency_phone', 'emergency_relation'],
  documents: [],
}

export function isAgreementSectionComplete(
  section: AgreementSectionKey,
  values: WizardFormValues,
): boolean {
  if (!SECTION_REQUIRED_FIELDS[section].every((field) => isFieldFilled(values[field]))) return false
  const issues = wizardIssueKeys(values)
  return SECTION_ISSUE_FIELDS[section].every((field) => !issues[field])
}

export function agreementSectionId(section: AgreementSectionKey): string {
  return `agreement-section-${section}`
}

/** Map server submit validation keys to agreement sections for scroll focus. */
export function agreementSectionForField(field: string): AgreementSectionKey | null {
  if (field === 'form') return null
  for (const section of AGREEMENT_SECTION_KEYS) {
    const required = SECTION_REQUIRED_FIELDS[section] as readonly string[]
    if (required.includes(field)) return section
    if (SECTION_ISSUE_FIELDS[section].includes(field)) return section
  }
  return null
}
